import { useEffect, useState, type FormEvent } from "react";

import type { AuthProviderId } from "./authProviders";

/**
 * The modal UI for `<SessionGuard>`. Two visual modes:
 *
 *   - "warning": idle limit approaching. Shows a countdown and a
 *     "Stay signed in" button that resets the timer + verifies the
 *     session is still alive.
 *   - "expired": session has ended (idle limit reached or
 *     `onAuthFailure` fired). Shows an inline re-auth UI tailored to
 *     the active provider — a single button for Entra (popup / bridge)
 *     and an email+password form for B2C ROPC.
 *
 * The modal is non-dismissable in both modes (no overlay-close, no
 * Escape). User must explicitly act.
 */

export type ModalMode = "warning" | "expired";

export interface SessionExpiryModalProps {
  mode: ModalMode;
  /** ms remaining until idle limit (warning mode only). */
  remainingMs: number;
  /** Active provider — picks the re-auth UI in expired mode. */
  selectedProvider: AuthProviderId | null;
  /** B2C accountLabel pre-fill (typically email). */
  accountLabel: string | null;

  // Warning-mode actions:
  onStaySignedIn: () => void;

  // Expired-mode actions:
  onSignBackInEntra: () => Promise<void>;
  onSignBackInB2c: (email: string, password: string) => Promise<void>;

  // Always available:
  onSignOut: () => void;
}

export default function SessionExpiryModal(props: SessionExpiryModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-card text-card-foreground border border-border shadow-2xl p-6 sm:p-7 flex flex-col gap-4">
        {props.mode === "warning" ? (
          <WarningBody
            remainingMs={props.remainingMs}
            onStaySignedIn={props.onStaySignedIn}
            onSignOut={props.onSignOut}
          />
        ) : (
          <ExpiredBody
            selectedProvider={props.selectedProvider}
            accountLabel={props.accountLabel}
            onSignBackInEntra={props.onSignBackInEntra}
            onSignBackInB2c={props.onSignBackInB2c}
            onSignOut={props.onSignOut}
          />
        )}
      </div>
    </div>
  );
}

function WarningBody({
  remainingMs,
  onStaySignedIn,
  onSignOut,
}: {
  remainingMs: number;
  onStaySignedIn: () => void;
  onSignOut: () => void;
}) {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const mm = Math.floor(seconds / 60);
  const ss = String(seconds % 60).padStart(2, "0");
  return (
    <>
      <h2
        id="session-modal-title"
        className="text-xl font-bold text-foreground m-0"
      >
        Are you still here?
      </h2>
      <p className="text-sm text-muted-foreground m-0">
        For your security you'll be signed out in{" "}
        <span className="font-mono font-semibold text-foreground">
          {mm}:{ss}
        </span>{" "}
        due to inactivity.
      </p>
      <div className="flex items-center gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={onSignOut}
          className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md"
        >
          Sign out
        </button>
        <button
          type="button"
          onClick={onStaySignedIn}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Stay signed in
        </button>
      </div>
    </>
  );
}

function ExpiredBody({
  selectedProvider,
  accountLabel,
  onSignBackInEntra,
  onSignBackInB2c,
  onSignOut,
}: {
  selectedProvider: AuthProviderId | null;
  accountLabel: string | null;
  onSignBackInEntra: () => Promise<void>;
  onSignBackInB2c: (email: string, password: string) => Promise<void>;
  onSignOut: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isB2c =
    selectedProvider === "pathways-b2c" ||
    selectedProvider === "provider-portal-b2c";

  const [email, setEmail] = useState(accountLabel ?? "");
  const [password, setPassword] = useState("");

  // If accountLabel arrives async (e.g., id_token decoded after mount),
  // populate the email field once.
  useEffect(() => {
    if (!email && accountLabel) setEmail(accountLabel);
  }, [accountLabel, email]);

  async function runEntra() {
    setError(null);
    setSubmitting(true);
    try {
      await onSignBackInEntra();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  }

  async function runB2c(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSignBackInB2c(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <h2
        id="session-modal-title"
        className="text-xl font-bold text-foreground m-0"
      >
        Your session has ended
      </h2>
      <p className="text-sm text-muted-foreground m-0">
        Sign back in to continue where you left off.
      </p>

      {isB2c ? (
        <form onSubmit={runB2c} className="flex flex-col gap-3 pt-1">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              className="rounded-xl border border-input px-4 py-2.5 text-base text-foreground bg-background hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all duration-150"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
              className="rounded-xl border border-input px-4 py-2.5 text-base text-foreground bg-background hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all duration-150"
            />
          </label>

          {error && (
            <p
              role="alert"
              className="text-sm text-destructive font-medium m-0"
            >
              {error}
            </p>
          )}

          <div className="flex items-center gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onSignOut}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md"
            >
              Sign out
            </button>
            <button
              type="submit"
              disabled={submitting || !email.trim() || !password}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {submitting ? "Signing in..." : "Sign back in"}
            </button>
          </div>
        </form>
      ) : (
        <>
          {error && (
            <p
              role="alert"
              className="text-sm text-destructive font-medium m-0"
            >
              {error}
            </p>
          )}
          <div className="flex items-center gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onSignOut}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md"
            >
              Sign out
            </button>
            <button
              type="button"
              onClick={runEntra}
              disabled={submitting}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {submitting ? "Signing in..." : "Sign back in"}
            </button>
          </div>
        </>
      )}
    </>
  );
}
